import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useOrganizationScope } from "../contexts/OrganizationScopeContext";
import { useLanguage } from "../contexts/LanguageContext";
import { Target, Plus, MoreVertical, Pencil, Trash2, Search, Eye, EyeOff, AlertTriangle, Calendar, Clock } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { toast } from "sonner";


// Scenario type options
const SCENARIO_TYPES = [
  { value: "STS-01", label: "STS-01" },
  { value: "STS-02", label: "STS-02" },
  { value: "PDRA-S01", label: "PDRA-S01" },
  { value: "PDRA-S02", label: "PDRA-S02" },
  { value: "custom", label: { cs: "Vlastní", en: "Custom" } }
];

// Approval status options
const APPROVAL_STATUSES = [
  { value: "draft", label: { cs: "Koncept", en: "Draft" }, color: "bg-slate-100 text-slate-600" },
  { value: "submitted", label: { cs: "Podáno", en: "Submitted" }, color: "bg-blue-100 text-blue-700" },
  { value: "approved", label: { cs: "Schváleno", en: "Approved" }, color: "bg-emerald-100 text-emerald-700" },
  { value: "expired", label: { cs: "Vypršelo", en: "Expired" }, color: "bg-amber-100 text-amber-700" },
  { value: "rejected", label: { cs: "Zamítnuto", en: "Rejected" }, color: "bg-red-100 text-red-700" }
];

export const Scenarios = () => {
  const { user } = useAuth();
  const { activeOrganizationId, isPlatformAdmin, registerScopeChangeGuard } = useOrganizationScope();
  const scopedOrganizationId = isPlatformAdmin ? activeOrganizationId : user?.organization_id;
  const { t, language } = useLanguage();
  const [scenarios, setScenarios] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editingScenario, setEditingScenario] = useState(null);
  const [formData, setFormData] = useState({
    organization_id: "",
    scenario_name: "",
    operation_type: "VLOS",
    key_risk_mitigations: "",
    required_authorization: "",
    // New fields
    scenario_type: "custom",
    ucl_reference: "",
    approval_status: "draft",
    approved_at: "",
    expires_at: "",
    conditions: ""
  });

  // Translations for new fields
  const tScenario = language === 'cs' ? {
    scenarioType: "Typ scénáře",
    uclReference: "ÚCL reference",
    uclReferencePlaceholder: "např. UCL-2025-STS-0042",
    approvalStatus: "Stav schválení",
    approvedAt: "Datum schválení",
    expiresAt: "Datum expirace",
    conditions: "Podmínky/omezení",
    conditionsPlaceholder: "Specifické podmínky stanovené ÚCL...",
    expiringWarning: "Expirace do 30 dnů",
    expiredWarning: "Autorizace vypršela",
    daysLeft: "dní zbývá",
    scenarioTypes: {
      "STS-01": "STS-01",
      "STS-02": "STS-02",
      "PDRA-S01": "PDRA-S01",
      "PDRA-S02": "PDRA-S02",
      "custom": "Vlastní"
    },
    approvalStatuses: {
      draft: "Koncept",
      submitted: "Podáno",
      approved: "Schváleno",
      expired: "Vypršelo",
      rejected: "Zamítnuto"
    },
    statsTitle: "Přehled scénářů",
    totalScenarios: "Celkem scénářů",
    approvedScenarios: "Schválených",
    expiringScenarios: "Brzy expiruje",
    expiredScenarios: "Vypršelo"
  } : {
    scenarioType: "Scenario Type",
    uclReference: "ÚCL Reference",
    uclReferencePlaceholder: "e.g. UCL-2025-STS-0042",
    approvalStatus: "Approval Status",
    approvedAt: "Approved Date",
    expiresAt: "Expires Date",
    conditions: "Conditions/Limitations",
    conditionsPlaceholder: "Specific conditions set by ÚCL...",
    expiringWarning: "Expires within 30 days",
    expiredWarning: "Authorization expired",
    daysLeft: "days left",
    scenarioTypes: {
      "STS-01": "STS-01",
      "STS-02": "STS-02",
      "PDRA-S01": "PDRA-S01",
      "PDRA-S02": "PDRA-S02",
      "custom": "Custom"
    },
    approvalStatuses: {
      draft: "Draft",
      submitted: "Submitted",
      approved: "Approved",
      expired: "Expired",
      rejected: "Rejected"
    },
    statsTitle: "Scenarios Overview",
    totalScenarios: "Total Scenarios",
    approvedScenarios: "Approved",
    expiringScenarios: "Expiring Soon",
    expiredScenarios: "Expired"
  };

  const fetchData = useCallback(async () => {
    try {
      const [scenariosRes, orgsRes, statsRes] = await Promise.all([
        api.get(`/scenarios`),
        api.get(`/organizations`),
        api.get(`/scenarios/stats`)
      ]);
      setScenarios(scenariosRes.data);
      setOrganizations(orgsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error(t.messages.fetchError);
    } finally {
      setLoading(false);
    }
  }, [ t.messages.fetchError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!showDialog) return undefined;
    return registerScopeChangeGuard(() => window.confirm(t.organizationScope?.formDirtyConfirm));
  }, [showDialog, registerScopeChangeGuard, t.organizationScope?.formDirtyConfirm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Frontend validation
    if (!formData.scenario_name.trim()) {
      toast.error(t.validation.enterScenarioName);
      return;
    }
    if (!formData.operation_type) {
      toast.error(t.validation.selectOperationType);
      return;
    }
    
    const payload = {
      ...formData,
      organization_id: scopedOrganizationId,
      // Convert empty strings and __none__ placeholder to null for optional fields
      required_authorization: (formData.required_authorization && formData.required_authorization !== "__none__") ? formData.required_authorization : null,
      key_risk_mitigations: formData.key_risk_mitigations || null,
      ucl_reference: formData.ucl_reference || null,
      approved_at: formData.approved_at || null,
      expires_at: formData.expires_at || null,
      conditions: formData.conditions || null
    };
    
    try {
      if (editingScenario) {
        await api.put(`/scenarios/${editingScenario.id}`, payload);
        toast.success(t.messages.scenarioUpdated);
      } else {
        await api.post(`/scenarios`, payload);
        toast.success(t.messages.scenarioCreated);
      }
      setShowDialog(false);
      setEditingScenario(null);
      resetForm();
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg || e.message || JSON.stringify(e)).join(', '));
      } else {
        toast.error(t.messages.saveError);
      }
    }
  };

  const handleDelete = async (scenario) => {
    if (!window.confirm(`${t.actions.delete} "${scenario.scenario_name}"?`)) return;
    try {
      await api.delete(`/scenarios/${scenario.id}`);
      toast.success(t.messages.scenarioDeleted);
      fetchData();
    } catch (error) {
      toast.error(t.messages.deleteError);
    }
  };

  const handleEdit = (scenario) => {
    setEditingScenario(scenario);
    setFormData({
      organization_id: scenario.organization_id,
      scenario_name: scenario.scenario_name,
      operation_type: scenario.operation_type,
      key_risk_mitigations: scenario.key_risk_mitigations || "",
      required_authorization: scenario.required_authorization || "",
      scenario_type: scenario.scenario_type || "custom",
      ucl_reference: scenario.ucl_reference || "",
      approval_status: scenario.approval_status || "draft",
      approved_at: scenario.approved_at ? scenario.approved_at.split('T')[0] : "",
      expires_at: scenario.expires_at ? scenario.expires_at.split('T')[0] : "",
      conditions: scenario.conditions || ""
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      organization_id: scopedOrganizationId || "",
      scenario_name: "",
      operation_type: "VLOS",
      key_risk_mitigations: "",
      required_authorization: "",
      scenario_type: "custom",
      ucl_reference: "",
      approval_status: "draft",
      approved_at: "",
      expires_at: "",
      conditions: ""
    });
  };

  const getOrgName = (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    return org?.company_name || "-";
  };

  const getScenarioTypeBadge = (type) => {
    const isSTS = type?.startsWith("STS") || type?.startsWith("PDRA");
    return (
      <Badge variant="outline" className={isSTS ? "bg-purple-50 text-purple-700 border-purple-300" : ""}>
        {tScenario.scenarioTypes[type] || type || tScenario.scenarioTypes.custom}
      </Badge>
    );
  };

  const getApprovalStatusBadge = (status) => {
    const statusConfig = APPROVAL_STATUSES.find(s => s.value === status) || APPROVAL_STATUSES[0];
    return (
      <Badge className={statusConfig.color}>
        {tScenario.approvalStatuses[status] || status}
      </Badge>
    );
  };

  const getExpirationInfo = (scenario) => {
    if (!scenario.expires_at || scenario.approval_status !== "approved") return null;
    
    try {
      const expDate = new Date(scenario.expires_at);
      const now = new Date();
      const daysLeft = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
      
      if (daysLeft < 0) {
        return (
          <Badge className="bg-red-100 text-red-700 gap-1">
            <AlertTriangle className="h-3 w-3" />
            {tScenario.expiredWarning}
          </Badge>
        );
      } else if (daysLeft <= 30) {
        return (
          <Badge className="bg-amber-100 text-amber-700 gap-1">
            <Clock className="h-3 w-3" />
            {daysLeft} {tScenario.daysLeft}
          </Badge>
        );
      }
    } catch {
      return null;
    }
    return null;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString(language === 'cs' ? 'cs-CZ' : 'en-US');
    } catch {
      return dateStr;
    }
  };

  const filteredScenarios = scenarios.filter(s =>
    s.scenario_name.toLowerCase().includes(search.toLowerCase()) ||
    (s.ucl_reference && s.ucl_reference.toLowerCase().includes(search.toLowerCase())) ||
    (s.scenario_type && s.scenario_type.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="scenarios-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            {t.complianceModule.scenarios}
          </h1>
          <p className="text-slate-500 mt-1">{t.pageDescriptions.scenarios}</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setEditingScenario(null); setShowDialog(true); }}
          className="gap-2 bg-primary hover:bg-primary/90"
          data-testid="add-scenario-btn"
        >
          <Plus className="h-4 w-4" /> {t.complianceModule.addScenario}
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{tScenario.totalScenarios}</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Target className="h-8 w-8 text-slate-300" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{tScenario.approvedScenarios}</p>
                  <p className="text-2xl font-bold text-emerald-600">{stats.by_status?.approved || 0}</p>
                </div>
                <Eye className="h-8 w-8 text-emerald-200" />
              </div>
            </CardContent>
          </Card>
          <Card className={stats.expiring_soon?.length > 0 ? "border-amber-300 bg-amber-50" : ""}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{tScenario.expiringScenarios}</p>
                  <p className="text-2xl font-bold text-amber-600">{stats.expiring_soon?.length || 0}</p>
                </div>
                <Clock className="h-8 w-8 text-amber-300" />
              </div>
            </CardContent>
          </Card>
          <Card className={stats.expired?.length > 0 ? "border-red-300 bg-red-50" : ""}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{tScenario.expiredScenarios}</p>
                  <p className="text-2xl font-bold text-red-600">{stats.expired?.length || 0}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-300" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Warning Banner */}
      {stats && (stats.expired?.length > 0 || stats.expiring_soon?.length > 0) && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">
                  {language === 'cs' ? 'Upozornění na autorizace scénářů' : 'Scenario Authorization Warnings'}
                </p>
                <ul className="mt-2 space-y-1 text-sm text-amber-700">
                  {stats.expired?.map(s => (
                    <li key={s.id}>• {s.scenario_name} ({s.scenario_type}) - {tScenario.expiredWarning}</li>
                  ))}
                  {stats.expiring_soon?.map(s => (
                    <li key={s.id}>• {s.scenario_name} ({s.scenario_type}) - {s.days_left} {tScenario.daysLeft}</li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder={t.complianceModule.searchScenarios}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-scenarios"
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t.complianceModule.scenarioName}</TableHead>
                <TableHead>{tScenario.scenarioType}</TableHead>
                {user?.role === "platform_admin" && <TableHead>{t.common.organization}</TableHead>}
                <TableHead>{t.complianceModule.operationType}</TableHead>
                <TableHead>{tScenario.approvalStatus}</TableHead>
                <TableHead>{tScenario.uclReference}</TableHead>
                <TableHead>{tScenario.expiresAt}</TableHead>
                <TableHead>{t.common.status}</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <div className="w-6 h-6 border-2 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  </TableCell>
                </TableRow>
              ) : filteredScenarios.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <Target className="h-12 w-12 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500">{t.complianceModule.noScenariosFound}</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredScenarios.map((scenario) => (
                  <TableRow key={scenario.id} data-testid={`scenario-row-${scenario.id}`}>
                    <TableCell className="font-medium">{scenario.scenario_name}</TableCell>
                    <TableCell>{getScenarioTypeBadge(scenario.scenario_type)}</TableCell>
                    {user?.role === "platform_admin" && (
                      <TableCell className="text-sm text-slate-500">{getOrgName(scenario.organization_id)}</TableCell>
                    )}
                    <TableCell>
                      <Badge variant="outline">{scenario.operation_type}</Badge>
                    </TableCell>
                    <TableCell>{getApprovalStatusBadge(scenario.approval_status)}</TableCell>
                    <TableCell className="text-sm text-slate-500 font-mono">
                      {scenario.ucl_reference || "-"}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <span className="text-sm">{formatDate(scenario.expires_at)}</span>
                        {getExpirationInfo(scenario)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {scenario.active_status ? (
                        <Badge className="bg-emerald-100 text-emerald-800">
                          <Eye className="h-3 w-3 mr-1" /> {t.complianceModule.active}
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600">
                          <EyeOff className="h-3 w-3 mr-1" /> {t.complianceModule.inactive}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`scenario-actions-${scenario.id}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(scenario)}>
                            <Pencil className="h-4 w-4 mr-2" /> {t.actions.edit}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(scenario)} className="text-red-600">
                            <Trash2 className="h-4 w-4 mr-2" /> {t.actions.delete}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingScenario ? t.dialogs.editScenario : t.dialogs.addScenario}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="scenario-form">
            {user?.role === "platform_admin" && (
              <div className="space-y-2">
                <Label>{t.common.organization} *</Label>
                <Select
                  value={formData.organization_id}
                  disabled
                  onValueChange={(value) => setFormData({ ...formData, organization_id: value })}
                >
                  <SelectTrigger data-testid="scenario-organization">
                    <SelectValue placeholder={t.placeholders.selectOrganization} />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>{org.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Scenario Name and Type */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="scenario_name">{t.complianceModule.scenarioName} *</Label>
                <Input
                  id="scenario_name"
                  value={formData.scenario_name}
                  onChange={(e) => setFormData({ ...formData, scenario_name: e.target.value })}
                  required
                  placeholder={t.complianceModule.scenarioNamePlaceholder}
                  data-testid="scenario-name"
                />
              </div>
              <div className="space-y-2">
                <Label>{tScenario.scenarioType}</Label>
                <Select
                  value={formData.scenario_type}
                  onValueChange={(value) => setFormData({ ...formData, scenario_type: value })}
                >
                  <SelectTrigger data-testid="scenario-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SCENARIO_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {typeof type.label === 'object' ? type.label[language] : type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Operation Type and Required Auth */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.complianceModule.operationType} *</Label>
                <Select
                  value={formData.operation_type}
                  onValueChange={(value) => setFormData({ ...formData, operation_type: value })}
                >
                  <SelectTrigger data-testid="scenario-operation-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="VLOS">VLOS</SelectItem>
                    <SelectItem value="BVLOS">BVLOS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t.complianceModule.requiredAuth}</Label>
                <Select
                  value={formData.required_authorization}
                  onValueChange={(value) => setFormData({ ...formData, required_authorization: value })}
                >
                  <SelectTrigger data-testid="scenario-authorization">
                    <SelectValue placeholder={t.complianceModule.selectType} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t.none}</SelectItem>
                    <SelectItem value="A1_A3">A1/A3</SelectItem>
                    <SelectItem value="A2">A2</SelectItem>
                    <SelectItem value="specific">{t.crew.authSpecific.split(' ')[0]}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* UCL Reference and Approval Status */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ucl_reference">{tScenario.uclReference}</Label>
                <Input
                  id="ucl_reference"
                  value={formData.ucl_reference}
                  onChange={(e) => setFormData({ ...formData, ucl_reference: e.target.value })}
                  placeholder={tScenario.uclReferencePlaceholder}
                  data-testid="scenario-ucl-reference"
                  className="font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label>{tScenario.approvalStatus}</Label>
                <Select
                  value={formData.approval_status}
                  onValueChange={(value) => setFormData({ ...formData, approval_status: value })}
                >
                  <SelectTrigger data-testid="scenario-approval-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {APPROVAL_STATUSES.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {typeof status.label === 'object' ? status.label[language] : status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="approved_at">{tScenario.approvedAt}</Label>
                <Input
                  id="approved_at"
                  type="date"
                  value={formData.approved_at}
                  onChange={(e) => setFormData({ ...formData, approved_at: e.target.value })}
                  data-testid="scenario-approved-at"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expires_at">{tScenario.expiresAt}</Label>
                <Input
                  id="expires_at"
                  type="date"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                  data-testid="scenario-expires-at"
                />
              </div>
            </div>

            {/* Conditions */}
            <div className="space-y-2">
              <Label htmlFor="conditions">{tScenario.conditions}</Label>
              <Textarea
                id="conditions"
                value={formData.conditions}
                onChange={(e) => setFormData({ ...formData, conditions: e.target.value })}
                placeholder={tScenario.conditionsPlaceholder}
                rows={2}
                data-testid="scenario-conditions"
              />
            </div>

            {/* Risk Mitigations */}
            <div className="space-y-2">
              <Label htmlFor="risk_mitigations">{t.complianceModule.riskMitigations}</Label>
              <Textarea
                id="risk_mitigations"
                value={formData.key_risk_mitigations}
                onChange={(e) => setFormData({ ...formData, key_risk_mitigations: e.target.value })}
                placeholder={t.complianceModule.riskMitigationsPlaceholder}
                rows={2}
                data-testid="scenario-mitigations"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>{t.actions.cancel}</Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="scenario-submit-btn">
                {editingScenario ? t.actions.update : t.actions.create}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
